/*
 * Copyright © 2020-2025 Ocean Galaxy Inc. All Rights Reserved.
 * @Description: 去水印任务处理 Hook
 * @LastEditors: yingongjing
 * @LastEditTime: 2026-01-07 20:39:42
 */

import { useCallback } from 'react'
import { useAITaskEvent } from './useAITaskEvent'
import { useUserId } from '../../../../app/hooks'
import { message } from 'antd'
import { WatermarkApplyStatus } from './useWatermarkApply'
import { fetchOutputFromUrl } from './useAITaskEvent'

// 定义去水印结果类型（新格式：返回ZIP文件）
interface WatermarkRemovalResult {
  res: string // ZIP文件URL
  success: number // 状态码：1-成功
  reason: string // 原因
  show_reason: string // 显示原因
}

// 定义去水印结果类型（旧格式：用于 removeWatermarksWithMapping，兼容旧接口）
interface WatermarkRemovalResultOld {
  src: string // 加密url
  res: string // 解密去盗图水印后的url, 默认或失败为原始url
  success: WatermarkApplyStatus // 状态码：0-编辑失败，1-已编辑，2-无修改项，3-编辑超时
  reason: string // 失败或错误原因
}

// 定义进度回调函数类型
export type RemovalProgressCallback = (current: number, total: number) => void

// 定义去水印结果类型（List格式：返回JSON List String）
interface WatermarkRemovalListResult {
  src: string // 原始URL
  res: string // 去水印后的URL
  success: number // 状态码：0-编辑失败，1-已编辑，2-无修改项，3-编辑超时
  reason?: string // 失败或错误原因
}

// 定义去水印 Hook 返回类型
export interface UseWatermarkRemovalReturn {
  // 执行去水印（新格式：返回ZIP文件URL）
  removeWatermarks: (
    urls: string[],
    onProgress?: RemovalProgressCallback
  ) => Promise<string | null> // 返回ZIP文件URL，失败返回null
  // 执行去水印并返回映射关系（原始URL -> 去水印后的URL）
  removeWatermarksWithMapping: (urls: string[]) => Promise<Map<string, string>> // 返回原始URL到去水印后URL的映射
  // 执行去水印并返回List格式（JSON List String）
  removeWatermarksAsList: (
    urls: string[],
    onProgress?: RemovalProgressCallback
  ) => Promise<WatermarkRemovalListResult[] | null> // 返回JSON List，失败返回null
}

/**
 * 去水印任务处理 Hook
 * 负责处理去水印的完整流程，包括创建任务、轮询结果、处理返回数据
 */
export const useWatermarkRemoval = (): UseWatermarkRemovalReturn => {
  // 使用 AI 任务 hook
  const { createAITask, pollAITaskResult } = useAITaskEvent()

  // 获取用户ID
  const userId = useUserId()

  /**
   * 执行去水印（新格式：返回ZIP文件URL）
   * @param urls 需要去水印的URL列表
   * @param onProgress 进度回调函数（可选）
   * @returns ZIP文件URL，失败返回null
   */
  const removeWatermarks = useCallback(
    async (
      urls: string[],
      onProgress?: RemovalProgressCallback
    ): Promise<string | null> => {
      if (urls.length === 0) {
        return null
      }

      try {
        // 创建AI任务
        const taskResult = await createAITask({
          aiTaskType: 16, // 去水印任务类型
          input: {
            urls: urls, // 需要去水印的url列表
            edit_user: userId || ''
          },
          userID: userId || '' // 使用从Redux状态获取的用户ID
        })

        // 计算超时时间（用于假的进度条）
        const timeout = Math.ceil(urls.length / 100) * 60 * 1000 // 毫秒
        const progressInterval = 200 // 进度更新间隔（毫秒）
        const progressStep = 100 / (timeout / progressInterval) // 每次更新的进度步长
        let fakeProgress = 0
        const maxProgress = 95 // 最大进度（永远不达到100%，直到任务完成）

        // 启动假的进度条
        const progressTimer = setInterval(() => {
          if (fakeProgress < maxProgress) {
            fakeProgress = Math.min(fakeProgress + progressStep, maxProgress)
            if (onProgress) {
              onProgress(Math.floor(fakeProgress), 100)
            }
          }
        }, progressInterval)

        try {
          // 轮询任务结果，根据照片数量动态计算超时时间
          const result = await pollAITaskResult(
            {
              taskID: taskResult?.data?.taskID || '',
              userID: userId || '' // 使用从Redux状态获取的用户ID
            },
            { photoCount: urls.length }
          )

          // 清除假的进度条
          clearInterval(progressTimer)

          // 检查任务状态
          const taskStatus = result.data?.taskStatus

          // 如果任务状态为4（处理失败），返回null
          if (taskStatus === 4) {
            message.error('去水印任务处理失败，请稍后重试')
            if (onProgress) {
              onProgress(100, 100) // 完成进度条
            }
            return null
          }

          // 如果任务状态为5（超时完成），返回null
          if (taskStatus === 5) {
            const errorMsg =
              result.data?.msg && result.data?.msg.length > 0
                ? result.data?.msg
                : '去水印任务超时，请稍后重试'
            message.warning(errorMsg)
            if (onProgress) {
              onProgress(100, 100) // 完成进度条
            }
            return null
          }

          // 根据结果处理ZIP文件URL
          if (result.data?.output && result.data?.taskStatus === 3) {
            try {
              let outputData: WatermarkRemovalResult

              // 判断output是否是URL
              const isUrl =
                typeof result.data.output === 'string' &&
                (result.data.output.startsWith('http://') ||
                  result.data.output.startsWith('https://'))

              if (isUrl) {
                // 如果是URL，从CDN获取内容
                outputData = await fetchOutputFromUrl<WatermarkRemovalResult>(
                  result.data.output
                )
              } else {
                // 如果不是URL，尝试直接解析为JSON对象
                try {
                  outputData =
                    typeof result.data.output === 'string'
                      ? JSON.parse(result.data.output)
                      : result.data.output
                } catch (parseError) {
                  console.error('去水印output格式错误:', result.data.output)
                  if (onProgress) {
                    onProgress(100, 100) // 完成进度条
                  }
                  return null
                }
              }

              // 新格式：返回ZIP文件URL
              if (outputData && outputData.res) {
                const zipUrl = outputData.res
                const success = Number(outputData.success)

                if (success === 1 && zipUrl.endsWith('.zip')) {
                  // 成功，返回ZIP文件URL
                  if (onProgress) {
                    onProgress(100, 100) // 完成进度条
                  }
                  return zipUrl
                } else {
                  // 失败
                  console.warn(
                    `去水印失败: ${outputData.reason || outputData.show_reason}`
                  )
                  if (onProgress) {
                    onProgress(100, 100) // 完成进度条
                  }
                  return null
                }
              } else {
                // outputData 格式错误
                console.warn('去水印output数据格式错误')
                if (onProgress) {
                  onProgress(100, 100) // 完成进度条
                }
                return null
              }
            } catch (error) {
              console.error('解析output数据失败:', error)
              if (onProgress) {
                onProgress(100, 100) // 完成进度条
              }
              return null
            }
          } else if (taskStatus === 3) {
            // 如果taskStatus为3但没有output，返回null
            message.warning('去水印任务完成但无结果')
            if (onProgress) {
              onProgress(100, 100) // 完成进度条
            }
            return null
          } else {
            // 其他状态（1-队列中，2-处理中等），返回null
            console.warn('useWatermarkRemoval: 任务状态未完成', {
              taskStatus,
              taskID: result.data?.taskID
            })
            message.warning('去水印任务状态异常')
            if (onProgress) {
              onProgress(100, 100) // 完成进度条
            }
            return null
          }
        } catch (error) {
          // 清除假的进度条
          clearInterval(progressTimer)
          throw error
        }
      } catch (error) {
        console.error('去水印任务失败:', error)
        message.error('去水印任务失败，请稍后重试')
        if (onProgress) {
          onProgress(100, 100) // 完成进度条
        }
        return null
      }
    },
    [createAITask, pollAITaskResult, userId]
  )

  /**
   * 执行去水印并返回映射关系
   * @param urls 需要去水印的URL列表
   * @returns 原始URL到去水印后URL的映射Map
   */
  const removeWatermarksWithMapping = useCallback(
    async (urls: string[]): Promise<Map<string, string>> => {
      const urlMap = new Map<string, string>()

      if (urls.length === 0) {
        return urlMap
      }

      try {
        // 创建AI任务
        const taskResult = await createAITask({
          aiTaskType: 16, // 去水印任务类型
          input: {
            urls: urls, // 需要去水印的url列表
            edit_user: userId || ''
          },
          userID: userId || '' // 使用从Redux状态获取的用户ID
        })

        // 轮询任务结果，根据照片数量动态计算超时时间
        const result = await pollAITaskResult(
          {
            taskID: taskResult?.data?.taskID || '',
            userID: userId || '' // 使用从Redux状态获取的用户ID
          },
          { photoCount: urls.length }
        )

        // 检查任务状态
        const taskStatus = result.data?.taskStatus

        // 如果任务状态为4（处理失败），返回空映射
        if (taskStatus === 4) {
          message.error('去水印任务处理失败，请稍后重试')
          // 失败时，使用原始URL作为映射值
          urls.forEach((url) => {
            urlMap.set(url, url)
          })
          return urlMap
        }

        // 如果任务状态为5（超时完成），返回原始URL映射
        if (taskStatus === 5) {
          const errorMsg =
            result.data?.msg && result.data?.msg.length > 0
              ? result.data?.msg
              : '去水印任务超时，请稍后重试'
          message.warning(errorMsg)
          // 超时时返回原始URL映射
          urls.forEach((url) => {
            urlMap.set(url, url)
          })
          return urlMap
        }

        // 根据结果处理去水印后的URL
        if (result.data?.output && result.data?.taskStatus === 3) {
          try {
            // 判断output是否是URL
            const isUrl =
              typeof result.data.output === 'string' &&
              (result.data.output.startsWith('http://') ||
                result.data.output.startsWith('https://'))

            if (!isUrl) {
              // 如果不是URL格式，说明output格式错误，返回原始URL映射
              console.error(
                '去水印output格式错误（不是URL格式），返回原始URL:',
                result.data.output
              )
              urls.forEach((url) => {
                urlMap.set(url, url)
              })
              return urlMap
            }

            // 如果是URL，从CDN获取内容
            let outputData: WatermarkRemovalResultOld[] | WatermarkRemovalResult

            if (isUrl) {
              outputData = await fetchOutputFromUrl<
                WatermarkRemovalResultOld[] | WatermarkRemovalResult
              >(result.data.output)
            } else {
              // 如果不是URL，尝试直接解析为JSON对象
              try {
                outputData =
                  typeof result.data.output === 'string'
                    ? JSON.parse(result.data.output)
                    : result.data.output
              } catch (parseError) {
                console.error('去水印output格式错误:', result.data.output)
                urls.forEach((url) => {
                  urlMap.set(url, url)
                })
                return urlMap
              }
            }

            // 判断是新格式（ZIP文件）还是旧格式（数组）
            if (Array.isArray(outputData)) {
              // 旧格式：数组，包含照片URL和对应的状态
              outputData.forEach((item) => {
                const removalStatus = Number(item.success)
                // 如果成功（状态为已编辑），使用处理后的URL，否则使用原始URL
                if (removalStatus === WatermarkApplyStatus.EDITED) {
                  urlMap.set(item.src, item.res)
                } else {
                  // 失败时，使用原始URL
                  urlMap.set(item.src, item.src)
                  console.warn(`去水印失败: ${item.reason}`)
                }
              })

              // 对于没有在outputData中出现的URL，使用原始URL作为映射值
              urls.forEach((url) => {
                if (!urlMap.has(url)) {
                  urlMap.set(url, url)
                }
              })

              return urlMap
            } else if (outputData && outputData.res) {
              // 新格式：ZIP文件，所有URL都映射到ZIP文件URL
              // 注意：新格式无法建立单个URL的映射，所以所有URL都映射到ZIP文件URL
              const zipUrl = outputData.res
              const success = Number(outputData.success)
              if (success === 1 && zipUrl.endsWith('.zip')) {
                urls.forEach((url) => {
                  urlMap.set(url, zipUrl)
                })
              } else {
                // 失败时，使用原始URL
                urls.forEach((url) => {
                  urlMap.set(url, url)
                })
                console.warn(
                  `去水印失败: ${outputData.reason || outputData.show_reason}`
                )
              }

              // 对于没有在outputData中出现的URL，使用原始URL作为映射值
              urls.forEach((url) => {
                if (!urlMap.has(url)) {
                  urlMap.set(url, url)
                }
              })

              return urlMap
            } else {
              // outputData 不是数组，返回原始URL映射
              console.warn('去水印output数据格式错误，返回原始URL')
              urls.forEach((url) => {
                urlMap.set(url, url)
              })
              return urlMap
            }
          } catch (error) {
            console.error(
              '解析output数据失败（output格式错误，返回原始URL）:',
              error
            )
            // output格式错误时，返回原始URL映射
            urls.forEach((url) => {
              urlMap.set(url, url)
            })
            return urlMap
          }
        } else if (taskStatus === 3) {
          // 如果taskStatus为3但没有output，返回原始URL映射
          message.warning('去水印任务完成但无结果，返回原始URL')
          urls.forEach((url) => {
            urlMap.set(url, url)
          })
          return urlMap
        } else {
          // 其他状态（1-队列中，2-处理中等），返回原始URL映射
          console.warn('useWatermarkRemoval: 任务状态未完成', {
            taskStatus,
            taskID: result.data?.taskID
          })
          message.warning('去水印任务状态异常，返回原始URL')
          urls.forEach((url) => {
            urlMap.set(url, url)
          })
          return urlMap
        }
      } catch (error) {
        console.error('去水印任务失败:', error)
        message.error('去水印任务失败，请稍后重试')
        // 失败时，使用原始URL作为映射值
        urls.forEach((url) => {
          urlMap.set(url, url)
        })
        return urlMap
      }
    },
    [createAITask, pollAITaskResult, userId]
  )

  /**
   * 执行去水印并返回List格式（JSON List String）
   * @param urls 需要去水印的URL列表
   * @param onProgress 进度回调函数（可选）
   * @returns JSON List数组，失败返回null
   */
  const removeWatermarksAsList = useCallback(
    async (
      urls: string[],
      onProgress?: RemovalProgressCallback
    ): Promise<WatermarkRemovalListResult[] | null> => {
      if (urls.length === 0) {
        return null
      }

      try {
        // 创建AI任务，传入 file_type: "list"
        const taskResult = await createAITask({
          aiTaskType: 16, // 去水印任务类型
          input: {
            urls: urls, // 需要去水印的url列表
            edit_user: userId || '',
            file_type: 'list' // 指定返回格式为 list
          },
          userID: userId || ''
        })

        // 计算超时时间（用于假的进度条）
        const timeout = Math.ceil(urls.length / 100) * 60 * 1000 // 毫秒
        const progressInterval = 200 // 进度更新间隔（毫秒）
        const progressStep = 100 / (timeout / progressInterval) // 每次更新的进度步长
        let fakeProgress = 0
        const maxProgress = 95 // 最大进度（永远不达到100%，直到任务完成）

        // 启动假的进度条
        const progressTimer = setInterval(() => {
          if (fakeProgress < maxProgress) {
            fakeProgress = Math.min(fakeProgress + progressStep, maxProgress)
            if (onProgress) {
              onProgress(Math.floor(fakeProgress), 100)
            }
          }
        }, progressInterval)

        try {
          // 轮询任务结果，根据照片数量动态计算超时时间
          const result = await pollAITaskResult(
            {
              taskID: taskResult?.data?.taskID || '',
              userID: userId || ''
            },
            { photoCount: urls.length }
          )

          // 清除假的进度条
          clearInterval(progressTimer)

          // 检查任务状态
          const taskStatus = result.data?.taskStatus

          // 如果任务状态为4（处理失败），返回null
          if (taskStatus === 4) {
            message.error('去水印任务处理失败，请稍后重试')
            if (onProgress) {
              onProgress(100, 100) // 完成进度条
            }
            return null
          }

          // 如果任务状态为5（超时完成），返回null
          if (taskStatus === 5) {
            const errorMsg =
              result.data?.msg && result.data?.msg.length > 0
                ? result.data?.msg
                : '去水印任务超时，请稍后重试'
            message.warning(errorMsg)
            if (onProgress) {
              onProgress(100, 100) // 完成进度条
            }
            return null
          }

          // 根据结果处理List格式数据
          if (result.data?.output && result.data?.taskStatus === 3) {
            try {
              let outputData:
                | WatermarkRemovalResult
                | WatermarkRemovalListResult[]

              // 判断output是否是URL
              const isUrl =
                typeof result.data.output === 'string' &&
                (result.data.output.startsWith('http://') ||
                  result.data.output.startsWith('https://'))

              if (isUrl) {
                // 如果是URL，从CDN获取内容
                outputData = await fetchOutputFromUrl<
                  WatermarkRemovalResult | WatermarkRemovalListResult[]
                >(result.data.output)
              } else {
                // 如果不是URL，尝试直接解析为JSON对象
                try {
                  outputData =
                    typeof result.data.output === 'string'
                      ? JSON.parse(result.data.output)
                      : result.data.output
                } catch (parseError) {
                  console.error('去水印output格式错误:', result.data.output)
                  if (onProgress) {
                    onProgress(100, 100) // 完成进度条
                  }
                  return null
                }
              }

              // 处理返回数据格式
              // 如果返回的是 WatermarkRemovalResult 格式（包含 res 字段且 res 是 JSON 字符串）
              if (
                outputData &&
                typeof outputData === 'object' &&
                'res' in outputData &&
                'success' in outputData
              ) {
                const removalResult = outputData as WatermarkRemovalResult
                const success = Number(removalResult.success)

                if (success === 1 && removalResult.res) {
                  // res 字段是 JSON 字符串数组，需要解析
                  try {
                    let urlList: string[]
                    if (typeof removalResult.res === 'string') {
                      // 尝试解析 JSON 字符串
                      urlList = JSON.parse(removalResult.res)
                    } else if (Array.isArray(removalResult.res)) {
                      // 如果已经是数组，直接使用
                      urlList = removalResult.res
                    } else {
                      throw new Error('res 字段格式错误')
                    }

                    // 验证解析结果
                    if (!Array.isArray(urlList)) {
                      throw new Error('解析后的 res 不是数组')
                    }

                    // 将 URL 列表转换为 WatermarkRemovalListResult 格式
                    // 注意：urlList 的顺序应该与 urls 的顺序一致
                    const listResults: WatermarkRemovalListResult[] = urls.map(
                      (url, index) => {
                        const processedUrl = urlList[index]
                        return {
                          src: url,
                          res: processedUrl || url, // 如果索引超出范围，使用原始URL
                          success: processedUrl ? 1 : 0,
                          reason: processedUrl ? '' : 'URL缺失'
                        }
                      }
                    )

                    if (onProgress) {
                      onProgress(100, 100) // 完成进度条
                    }
                    return listResults
                  } catch (parseError) {
                    console.error('解析 res JSON 字符串失败:', parseError)
                    console.error('res 内容:', removalResult.res)
                    if (onProgress) {
                      onProgress(100, 100) // 完成进度条
                    }
                    return null
                  }
                } else {
                  const errorMsg =
                    removalResult.reason ||
                    removalResult.show_reason ||
                    '去水印任务失败'
                  console.error('去水印任务失败:', errorMsg)
                  message.error(errorMsg)
                  if (onProgress) {
                    onProgress(100, 100) // 完成进度条
                  }
                  return null
                }
              } else if (Array.isArray(outputData)) {
                // 如果直接返回数组格式
                if (onProgress) {
                  onProgress(100, 100) // 完成进度条
                }
                return outputData as WatermarkRemovalListResult[]
              } else {
                console.error('去水印output数据格式错误:', outputData)
                if (onProgress) {
                  onProgress(100, 100) // 完成进度条
                }
                return null
              }
            } catch (error) {
              console.error('处理去水印结果失败:', error)
              if (onProgress) {
                onProgress(100, 100) // 完成进度条
              }
              return null
            }
          } else {
            console.error('去水印任务结果异常:', result.data)
            if (onProgress) {
              onProgress(100, 100) // 完成进度条
            }
            return null
          }
        } catch (error) {
          clearInterval(progressTimer)
          console.error('轮询去水印任务失败:', error)
          if (onProgress) {
            onProgress(100, 100) // 完成进度条
          }
          return null
        }
      } catch (error) {
        console.error('创建去水印任务失败:', error)
        if (onProgress) {
          onProgress(100, 100) // 完成进度条
        }
        return null
      }
    },
    [createAITask, pollAITaskResult, userId]
  )

  return {
    removeWatermarks,
    removeWatermarksWithMapping,
    removeWatermarksAsList
  }
}

export default useWatermarkRemoval
